// Reusable FilterBar component for consistent filter UI across the application
// Based on the transaction filter bar design with Apple-inspired minimalist styling
// 
// Usage:
//   <FilterBar filters={[
//     { type: 'select', label: 'Status', value: status, onChange: setStatus, options: [...] },
//     { type: 'react-select', label: 'Branch', value: branch, onChange: setBranch, options: [...] },
//   ]} />

import React from "react";
import Select from "react-select";
import { CalendarDays, ChevronLeft, ChevronRight, Search } from "lucide-react";

function DateFilterInput({ label, value, onChange, min, max, minWidth, className, compact = false }) {
    const inputRef = React.useRef(null);

    const openPicker = () => {
        const input = inputRef.current;
        if (!input) return;

        input.focus();
        if (typeof input.showPicker === "function") {
            input.showPicker();
            return;
        }
        input.click();
    };

    return (
        <div className={`shrink-0 flex flex-col ${className}`} style={{ minWidth, ...(compact ? { width: minWidth } : {}) }}>
            <label className={`${compact ? "text-[11px] mb-0.5" : "text-[13px] mb-1"} font-semibold text-gray-700 pl-1`}>{label}</label>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="date"
                    className="h-10 w-full appearance-none rounded-xl border-0 bg-[#f6f6f8] py-2 pl-3 pr-11 text-[15px] text-gray-900 focus:ring-2 focus:ring-primary"
                    style={compact ? { height: 36, fontSize: 13, paddingLeft: 8, paddingRight: 36 } : undefined}
                    aria-label={label}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    min={min}
                    max={max}
                />
                <button
                    type="button"
                    onClick={openPicker}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 hover:bg-white hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label={`Open ${label} picker`}
                    tabIndex={-1}
                >
                    <CalendarDays size={18} strokeWidth={2} />
                </button>
            </div>
        </div>
    );
}

/**
 * FilterBar Component
 * @param {Array} filters - Array of filter configurations
 * @param {ReactNode} extraActions - Additional buttons/actions to display at the end (e.g., view toggles)
 */
function FilterBar({ filters = [], extraActions = null, compact = false }) {
    const scrollRef = React.useRef(null);
    const [showLeftHint, setShowLeftHint] = React.useState(false);
    const [showRightHint, setShowRightHint] = React.useState(false);

    React.useEffect(() => {
        const node = scrollRef.current;
        if (!node) return;

        const updateHints = () => {
            const isMobile = window.innerWidth < 768;
            if (!compact && !isMobile) {
                setShowLeftHint(false);
                setShowRightHint(false);
                return;
            }

            const { scrollLeft, scrollWidth, clientWidth } = node;
            const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
            const threshold = 8;

            setShowLeftHint(scrollLeft > threshold);
            setShowRightHint(scrollLeft < maxScrollLeft - threshold);
        };

        updateHints();
        node.addEventListener("scroll", updateHints, { passive: true });
        window.addEventListener("resize", updateHints);
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateHints) : null;
        observer?.observe(node);
        if (node.firstElementChild) observer?.observe(node.firstElementChild);

        return () => {
            node.removeEventListener("scroll", updateHints);
            window.removeEventListener("resize", updateHints);
            observer?.disconnect();
        };
    }, [filters.length, extraActions, compact]);

    const scrollFilters = (direction) => {
        const node = scrollRef.current;
        if (node) node.scrollBy({ left: direction * Math.max(180, node.clientWidth * 0.7), behavior: "smooth" });
    };

    // Shared styles for react-select
    const reactSelectStyles = {
        control: (base) => ({
            ...base,
            background: '#f6f6f8',
            border: 'none',
            boxShadow: 'none',
            minHeight: 36,
            borderRadius: 10,
            fontSize: compact ? 13 : 15,
        }),
        ...(compact ? {
            valueContainer: (base) => ({ ...base, padding: '0 6px' }),
            dropdownIndicator: (base) => ({ ...base, padding: 4, '& svg': { width: 16, height: 16 } }),
            indicatorSeparator: () => ({ display: 'none' }),
            input: (base) => ({ ...base, margin: 0, padding: 0 }),
        } : {}),
        menu: (base) => ({ ...base, borderRadius: 10, fontSize: 15, zIndex: 30 }),
        menuPortal: (base) => ({ ...base, zIndex: 30 }),
        option: (base, state) => ({
            ...base,
            background: state.isSelected ? '#e5e5ea' : state.isFocused ? '#f0f0f3' : '#fff',
            color: '#222',
            borderRadius: 8,
            fontSize: 15,
        }),
    };

    return (
        <div className={`relative mb-1 rounded-xl bg-[#f6f6f8] shadow-md ${compact && extraActions ? "pr-24" : ""}`}>
            <div
                ref={scrollRef}
                className={`filterbar-scroll overflow-x-auto overflow-y-hidden rounded-xl p-2 ${compact ? "" : "md:overflow-x-visible md:overflow-y-visible"}`}
            >
                <div className={`flex min-w-max flex-nowrap items-end ${compact ? "gap-2" : "gap-3 pr-24 md:min-w-0 md:flex-wrap md:pr-0"}`}>
            {filters.map((filter, index) => {
                const { type, label, value, onChange, options, placeholder, isSearchable = false, minWidth = '120px', hide = false, className = '', min, max } = filter;

                // Skip hidden filters
                if (hide) return null;
                const fieldStyle = { minWidth, ...(compact ? { width: minWidth } : {}) };
                const labelClass = `${compact ? "text-[11px] mb-0.5" : "text-[13px] mb-1"} font-semibold text-gray-700 pl-1`;

                // Native select dropdown
                if (type === 'select') {
                    return (
                        <div key={index} className={`shrink-0 flex flex-col ${className}`} style={fieldStyle}>
                            <label className={labelClass}>{label}</label>
                            <select
                                aria-label={label}
                                style={compact ? { height: 36, fontSize: 13, padding: '4px 8px' } : undefined}
                                className="bg-[#f6f6f8] rounded-xl px-3 py-2 text-[15px] border-0 focus:ring-2 focus:ring-primary"
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                            >
                                {options.map((opt, idx) => (
                                    <option key={idx} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                }

                // React-select dropdown (for searchable/complex options)
                if (type === 'react-select') {
                    return (
                        <div key={index} className={`shrink-0 flex flex-col ${className}`} style={fieldStyle}>
                            <label className={labelClass}>{label}</label>
                            <Select
                                aria-label={label}
                                classNamePrefix="react-select"
                                styles={reactSelectStyles}
                                options={options}
                                value={value}
                                onChange={(opt) => onChange(opt ? opt.value : '')}
                                isSearchable={isSearchable}
                                placeholder={placeholder || label}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    );
                }

                if (type === 'date') {
                    return (
                        <DateFilterInput
                            key={index}
                            label={label}
                            value={value}
                            onChange={onChange}
                            min={min}
                            max={max}
                            minWidth={minWidth}
                            className={className}
                            compact={compact}
                        />
                    );
                }

                if (type === 'text') {
                    return (
                        <div key={index} className={`shrink-0 flex flex-col ${className}`} style={fieldStyle}>
                            <label className={labelClass}>{label}</label>
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    aria-label={label}
                                    style={compact ? { height: 36, fontSize: 13 } : undefined}
                                    type="search"
                                    value={value || ""}
                                    onChange={(e) => onChange(e.target.value)}
                                    placeholder={placeholder || label}
                                    className="h-10 w-full rounded-xl border-0 bg-[#f6f6f8] py-2 pl-9 pr-3 text-[15px] text-gray-900 outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-primary"
                                />
                            </div>
                        </div>
                    );
                }

                return null;
            })}

            {/* Extra actions (e.g., view toggles) */}
            {extraActions && !compact && (
                <div className="ml-auto hidden shrink-0 gap-2 md:flex">
                    {extraActions}
                </div>
            )}
                </div>
            </div>

            {extraActions && (
                <div className={`absolute bottom-2 right-2 z-20 flex gap-2 ${compact ? "" : "md:hidden"}`}>
                    {extraActions}
                </div>
            )}

            {showLeftHint && (
                <div className={`absolute left-2 top-1 z-10 ${compact ? "" : "md:hidden"}`}>
                    <button type="button" onClick={() => scrollFilters(-1)} aria-label="Scroll filters back" className="filterbar-scroll-hint flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold">
                        <ChevronLeft className="h-3 w-3 animate-[bounceLeft_1.2s_ease-in-out_infinite]" />
                        <span>Back</span>
                    </button>
                </div>
            )}

            {showRightHint && (
            <div className={`absolute top-1 z-10 ${compact ? (extraActions ? "right-24" : "right-2") : "right-2 md:hidden"}`}>
                <button type="button" onClick={() => scrollFilters(1)} aria-label="Scroll to more filters" className="filterbar-scroll-hint flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold">
                    <span>More</span>
                    <ChevronRight className="h-3 w-3 animate-[bounceX_1.2s_ease-in-out_infinite]" />
                </button>
            </div>
            )}

            <style>{`
                .filterbar-scroll-hint {
                    background: var(--color-primary);
                    color: var(--color-on-primary, #fff);
                    border: 1px solid color-mix(in srgb, var(--color-primary) 65%, white);
                    animation: filterHintPulse 1.6s ease-in-out infinite;
                }
                .filterbar-scroll-hint > span,
                .filterbar-scroll-hint > svg {
                    color: inherit;
                }
                .filterbar-scroll-hint:focus-visible {
                    outline: 2px solid var(--color-primary);
                    outline-offset: 3px;
                }
                @keyframes filterHintPulse {
                    0%, 100% {
                        background: var(--color-primary);
                        box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 55%, transparent);
                    }
                    50% {
                        background: color-mix(in srgb, var(--color-primary) 72%, white);
                        box-shadow: 0 0 0 5px color-mix(in srgb, var(--color-primary) 20%, transparent);
                    }
                }
                @media (prefers-reduced-motion: reduce) {
                    .filterbar-scroll-hint,
                    .filterbar-scroll-hint > svg {
                        animation: none;
                    }
                }
                .filterbar-scroll {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .filterbar-scroll::-webkit-scrollbar {
                    display: none;
                }
                @keyframes bounceX {
                    0%, 100% { transform: translateX(0); }
                    50% { transform: translateX(3px); }
                }
                @keyframes bounceLeft {
                    0%, 100% { transform: translateX(0); }
                    50% { transform: translateX(-3px); }
                }
            `}</style>
        </div>
    );
}

export default FilterBar;
